import { useState, useEffect } from "react"
import axios from "axios"
import cache from '@services/cache'
import { useHistory, useLocation } from "react-router-dom"
import { route } from "@helpers"

export default <T extends any, I extends any = undefined>(url: string, { 
    initial, 
    dependencies = [], 
    cacheId, 
    reload = true, 
    params = [] 
}: Options<I> = {}) => {
    const location = useLocation()
    const history = useHistory()
    const cachedData = cacheId ? cache.get(cacheId) : null

    const [response, setResponse] = useState<Response<T>>({
        data: cachedData ?? initial,
        status: cachedData ? (reload ? "cache" : "loaded") : "loading"
    })

    const setData: React.Dispatch<React.SetStateAction<T>> = (action: React.SetStateAction<T | null | undefined>) => {
        setResponse(old => {
            let data: T | null | undefined = (action instanceof Function) 
                ? action(old.data)
                : action

            if (cacheId) cache.add(cacheId, data)

            return { ...old, data }
        })
    }

    const request = async () => {
        try {
            const result = await axios.get<T>(url, { params })
            return result.data || null
        } catch (e) {
            if (e.response?.status === 404)
                history.replace(route("404"), { from: location })
            
            throw e
        }
    }

    useEffect(() => {
        let isMounted = true

        // if cache exists & reload is not required, skips the request
        if (cachedData && !reload) return

        request().then(data => {
            if (isMounted) setResponse({ data, status: "loaded" })
            if (cacheId) cache.add(cacheId, data)
        })

        return () => { isMounted = false }
    }, dependencies)

    return { 
        data: response.data as T | I,
        status: response.status,
        setData
    }
}

// TYPES
interface Response<T> {
    data?: T | null,
    status: Interfaces.DataStatus
}

interface Options<I extends any> {
    initial?: I,
    dependencies?: [],
    cacheId?: string,
    reload?: boolean,
    params?: []
}